import React, { useState, startTransition, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOidc } from '@axa-fr/react-oidc';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

import LottieAnimation from '../../CustomComponents/LottieAnimation';

import kudzuLogo from '../../../assets/images/kudzu_logo.svg';
import ArrowPrev from '../../../assets/images/arrow_prev.png';
import ArrowNext from '../../../assets/images/arrow_next.png';

import Animation1 from '../../../assets/animations/Illustration_animation_1.json';
import Animation2 from '../../../assets/animations/Illustration_animation_2.json';
import Animation3 from '../../../assets/animations/Illustration_animation_3.json';
import Animation4 from '../../../assets/animations/Illustration_animation_4.json';
import Animation5 from '../../../assets/animations/Illustration_animation_5.json';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';

const SlideLayout = ({ slide, slideIndex, currentSlide }) => {
  const { t } = useTranslation();
  
  return (
    <div className="md:h-full">
      <div className="flex flex-col md:flex-row md:h-full">
        <div className="md:flex-1 bg-[#0C4437] flex flex-col justify-center items-center md:justify-start md:items-start text-center md:text-left p-4 md:px-[3%] md:py-4">
          <img 
            src={kudzuLogo} 
            alt="Welcome to Kudzu" 
            className="w-[100px] h-[28px] md:w-[116px] md:h-[32px] object-contain mt-[12px] mb-[20px] md:mt-[20px] md:mb-[50px]"
          />
          <div className="w-full max-w-[280px] md:max-w-[426px] text-white font-bitter font-semibold text-[22px] md:text-[clamp(28px,2.2vw,36px)] lg:text-[clamp(32px,2.4vw,40px)] xl:text-[clamp(36px,2.6vw,44px)] 2xl:text-[clamp(40px,2.8vw,48px)] leading-[130%] mb-[12px] md:mb-[20px] md:whitespace-pre text-center md:text-left">
            {t('where_money_grows_with_you')}
          </div>
          <div className="max-w-[392px] md:max-w-[38vw] lg:max-w-[42vw] text-white font-hanken-grotesk font-normal text-[14px] leading-[20px] md:text-[clamp(18px,1.4vw,22px)] md:leading-[clamp(27px,2.0vw,32px)] lg:text-[clamp(20px,1.5vw,24px)] lg:leading-[clamp(30px,2.2vw,36px)] xl:text-[clamp(22px,1.6vw,26px)] xl:leading-[clamp(33px,2.4vw,40px)] 2xl:text-[clamp(24px,1.8vw,28px)] 2xl:leading-[clamp(36px,2.6vw,42px)] whitespace-pre-line">
            {t('financial_tools_description')}
          </div>
          
          <div className="hidden md:block mt-auto pb-[7vh] lg:pb-[8vh] mb-[3vh]">
            {slide.disclaimers.map((disclaimer, idx) => (
              <div key={`disclaimer-${slideIndex}-${idx}`} className={`text-[#FEFAEECC] font-hanken-grotesk text-[clamp(11px,0.8vw,14px)] leading-[clamp(16px,1.2vw,20px)] ${disclaimer.isBold ? 'font-medium' : 'font-normal'} ${idx > 0 ? 'lg:mt-[0.6vh] md:mt-[0.5vh]' : ''} lg:max-w-[40vw] md:max-w-[35vw]`}>
                {disclaimer.link ? (
                  <>
                    {disclaimer.text.before}
                    <a 
                      href={disclaimer.link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline cursor-pointer hover:opacity-75"
                    >
                      {disclaimer.link.text}
                    </a>
                    {disclaimer.text.after}
                  </>
                ) : disclaimer.text}
              </div>
            ))}
          </div>
        </div>
        
        <div className="md:flex-1 bg-[#FEFAEE] flex flex-col justify-center items-center md:py-0 md:h-full md:min-h-full">
          
          <div className="md:hidden w-full flex flex-col items-center justify-center px-4 pt-[3vh] pb-[1vh] min-h-[400px]">
            <div className="w-full flex flex-col items-center justify-center text-center flex-1">
              <div className="w-full flex justify-center items-center mb-4 flex-shrink-0">
                <div className="w-[75vw] max-w-[420px] sm:w-[65vw] sm:max-w-[500px] aspect-[4/3] bg-[#FEFAEE] overflow-hidden">
                  <LottieAnimation
                    animationData={slide.animation}
                    isActive={slideIndex === currentSlide}
                    className="w-full h-full"
                    slideIndex={slideIndex}
                    style={slideIndex === 0 ? { transform: 'scale(1.25)' } : { transform: 'scale(1.1)' }}
                  />
                </div>
              </div>
              
              <div className="w-full flex flex-col items-center flex-shrink-0">
                <h1 className="font-bitter font-medium text-[clamp(22px,6vw,28px)] leading-[130%] mb-2 text-center text-[#0C4437] px-4">
                  {slide.content.title.first.split('\n').map((line, idx) => (
                    <React.Fragment key={`mobile-title-first-${slideIndex}-${line}`}>
                      {idx > 0 && <br />}
                      {line}
                    </React.Fragment>
                  ))}
                  {slide.content.title.second && (
                    <>
                      {!slide.content.title.first.includes('\n') && !slide.content.title.second.includes('\n') && <br />}
                      <span className="text-[#2965FB]">
                        {slide.content.title.first.includes('\n') || slide.content.title.second.includes('\n') ? ' ' : ''}
                        {slide.content.title.second.split('\n').map((line, idx) => (
                          <React.Fragment key={`mobile-title-second-${slideIndex}-${line}`}>
                            {idx > 0 && <br />}
                            {line}
                          </React.Fragment>
                        ))}
                      </span>
                    </>
                  )}
                </h1>
                
                <p className="font-hanken-grotesk font-normal text-[14px] leading-[150%] text-center text-[#262B3A]/70 max-w-[300px]">
                  {slide.content.description.replace(/\n/g, ' ')}
                </p>
              </div>
            </div>
          </div>
          
          <div className="md:flex hidden flex-col justify-center items-center h-full px-[2vw] py-[3vh]">
            <div
              className="
                relative
                mb-[2vh]
                h-[clamp(240px,30vh,320px)]
                lg:h-[clamp(280px,35vh,380px)]
                xl:h-[clamp(320px,38vh,420px)]
                2xl:h-[clamp(360px,40vh,460px)]
                w-full
                flex
                justify-center
                items-center
                overflow-hidden
                pointer-events-none
              "
            >
              <div className="relative h-full aspect-[4/3]">
                <LottieAnimation
                  animationData={slide.animation}
                  isActive={slideIndex === currentSlide}
                  slideIndex={slideIndex}
                  className={`
                    absolute
                    left-1/2
                    top-1/2
                    -translate-x-1/2
                    -translate-y-1/2

                    ${
                      slideIndex === 0
                        ? `scale-[clamp(1.15,1.25,1.4)]`
                        : `scale-[clamp(0.95,1.05,1.2)]`
                    }

                    w-auto
                    h-auto
                    max-h-full
                    max-w-full
                    pointer-events-none
                    object-contain
                  `}
                />
              </div>
            </div>
            
            <div className="text-center max-w-[520px] lg:max-w-[580px] xl:max-w-[640px] 2xl:max-w-[700px] w-full">
              <h1 className="font-bitter font-semibold text-[24px] md:text-[clamp(24px,2.0vw,32px)] lg:text-[clamp(28px,2.2vw,36px)] xl:text-[clamp(32px,2.4vw,40px)] 2xl:text-[clamp(36px,2.6vw,44px)] leading-[130%] tracking-[0%] mb-4 text-center whitespace-pre-line">
                <span className="text-[#0C4437]">{slide.content.title.first}</span>
                {slide.content.title.second && (
                  <>
                    {!slide.content.title.first.includes('\n') && !slide.content.title.second.includes('\n') && <br />}
                    <span className="text-[#2965FB]">
                      {slide.content.title.first.includes('\n') || slide.content.title.second.includes('\n') ? ` ${slide.content.title.second}` : slide.content.title.second}
                    </span>
                  </>
                )}
              </h1>
              <div className="text-[#262B3AB2] font-hanken-grotesk font-normal text-[clamp(14px,1.4vw,20px)] leading-[clamp(20px,1.8vw,28px)] word-break-normal">
                <p>
                  {slide.content.description.split('\n').map((line, index) => (
                    <span key={`desktop-desc-${slideIndex}-${line.trim()}`}>
                      {line.trim()}
                      {index < slide.content.description.split('\n').length - 1 && <br />}
                    </span>
                  ))}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LandingCarousel = ({ onSkipClick, onKudzuPlusClick }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useOidc();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);
  const isFromSmiOne = useSelector((state) => state.fromSmiOne);
  const dispatch = useDispatch();

  useDocumentTitle(t('page_title_get_started'));

  // Update isMobile on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const landingSlidesData = [
    {
      animation: Animation1,
      content: {
        title: {
          first: t('bank_with_confidence_first'),
          second: t('bank_with_confidence_second')
        },
        description: t('no_fees_details')
      },
      disclaimers: [
        { 
          text: t('banking_disclaimer'), 
          isBold: false 
        },
        { 
          text: {
            before: t('fees_disclaimer_before'),
            after: t('fees_disclaimer_after')
          },
          link: {
            url: 'https://kudzumoney.com/policies/#kudzu-deposit-account-agreement',
            text: t('deposit_agreement_link')
          },
          isBold: true 
        }
      ],
      mobileDisclaimers: [
        { text: t('banking_disclaimer_mobile') },
        { 
          text: {
            before: t('fees_disclaimer_before'),
            after: t('fees_disclaimer_after')
          },
          link: {
            url: 'https://kudzumoney.com/policies/#kudzu-deposit-account-agreement',
            text: t('deposit_agreement_link')
          }
        }
      ]
    },
    {
      animation: Animation2,
      content: {
        title: {
          first: t('slide2_debit_card_first'),
          second: t('slide2_debit_card_second')
        },
        description: t('slide2_additional_text')
      },
      disclaimers: [
        { 
          text: t('slide2_banking_text'), 
          isBold: false 
        },
        { 
          text: t('slide2_visa_text'), 
          isBold: false 
        }
      ],
      mobileDisclaimers: [
        { text: t('slide2_banking_text') },
        { text: t('slide2_visa_text_mobile') }
      ]
    },
    {
      // image: Group3,
      animation: Animation3,
      content: {
        title: {
          first: t('slide3_savings_text_first'),
          second: t('slide3_savings_text_second')
        },
        description: t('slide3_additional_text')
      },
      disclaimers: [
        { 
          text: {
            before: t('slide3_apy_text'),
            after: ''
          },
          link: {
            url: 'https://kudzumoney.com/policies/#savings-rate-information',
            text: t('see_full_details')
          },
          isBold: false 
        }
      ],
      mobileDisclaimers: [
        { 
          text: {
            before: t('slide3_apy_text_mobile'),
            after: ''
          },
          link: {
            url: 'https://kudzumoney.com/policies/#savings-rate-information',
            text: t('see_full_details')
          }
        }
      ]
    },
    {
      animation: Animation4,
      content: {
        title: {
          first: t('slide4_budget_text_first'),
          second: t('slide4_budget_text_second')
        },
        description: t('slide4_additional_text')
      },
      disclaimers: [
        { 
          text: t('slide4_message_rates'), 
          isBold: false 
        },
        { 
          text: {
            before: t('slide4_covered_by_kudzu'),
            after: ''
          },
          link: {
            url: 'https://kudzumoney.com/policies/#covered-by-kudzu-terms-and-conditions',
            text: t('see_full_terms')
          },
          isBold: false 
        }
      ],
      mobileDisclaimers: [
        { text: t('slide4_message_rates') },
        { 
          text: {
            before: t('slide4_covered_by_kudzu'),
            after: ''
          },
          link: {
            url: 'https://kudzumoney.com/policies/#covered-by-kudzu-terms-and-conditions',
            text: t('see_full_terms')
          }
        }
      ]
    },
    {
      animation: Animation5,
      content: {
        title: {
          first: t('slide5_payperks_text_first'),
          second: t('slide5_payperks_text_second')
        },
        description: t('slide5_additional_text')
      },
      disclaimers: [
        { 
          text: t('slide5_bancorp_disclaimer'), 
          isBold: false 
        },
        { 
          text: {
            before: t('slide5_sweepstakes_before'),
            after: t('slide5_sweepstakes_after')
          },
          link: {
            url: 'https://kudzumoney.com/policies/#official-sweepstakes-rules',
            text: t('slide5_sweepstakes_link')
          },
          isBold: false 
        }
      ],
      mobileDisclaimers: [
        { text: t('slide5_bancorp_disclaimer_mobile') },
        { 
          text: {
            before: t('slide5_sweepstakes_before'),
            after: t('slide5_sweepstakes_after_mobile')
          },
          link: {
            url: 'https://kudzumoney.com/policies/#official-sweepstakes-rules',
            text: t('slide5_sweepstakes_link')
          }
        }
      ]
    }
  ];

  const handleLoginClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    startTransition(() => {
      if (onSkipClick) {
        onSkipClick();
      }
      login('/');
    });
  };

  const handleSignupClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    startTransition(() => {
      if (isFromSmiOne) {
        dispatch({ type: "MAKE_OPEN_SMI_ONBOARD", payload: { makeOpenSmiOnborad: true } });
      }
      sessionStorage.setItem('fromLandingCarousel', 'true');
      sessionStorage.setItem('returningToLandingCarousel', 'true');
      // Close the carousel modal first
      if (onSkipClick) {
        onSkipClick();
      }
      navigate('/signup', { 
        state: { 
          directSignup: true,
          fromLandingCarousel: true 
        } 
      });
    });
  };

  const goToPrevSlide = () => {
    const newSlide = currentSlide === 0 ? landingSlidesData.length - 1 : currentSlide - 1;
    setCurrentSlide(newSlide);
  };

  const goToNextSlide = () => {
    const newSlide = currentSlide === landingSlidesData.length - 1 ? 0 : currentSlide + 1;
    setCurrentSlide(newSlide);
  };

  const handleTouchStart = (e) => {
    if (!e.targetTouches || e.targetTouches.length === 0) return;
    const touch = e.targetTouches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY
    };
    touchEndRef.current = {
      x: touch.clientX,
      y: touch.clientY
    };
  };

  const handleTouchMove = (e) => {
    if (!e.targetTouches || e.targetTouches.length === 0 || !touchStartRef.current) return;
    const touch = e.targetTouches[0];
    touchEndRef.current = {
      x: touch.clientX,
      y: touch.clientY
    };
  };

  const handleTouchEnd = () => {
    if (!touchStartRef.current || !touchEndRef.current) {
      touchStartRef.current = null;
      touchEndRef.current = null;
      return;
    }

    const deltaX = touchStartRef.current.x - touchEndRef.current.x;
    const deltaY = Math.abs(touchStartRef.current.y - touchEndRef.current.y);
    const threshold = window.innerWidth * 0.1;
    
    if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > threshold) {
      if (deltaX > 0) {
        goToNextSlide();
      } else {
        goToPrevSlide();
      }
    }

    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  const handleBottomTouchStart = (e) => {
    const isButton = e.target.tagName === 'BUTTON' || e.target.closest('button');
    if (isButton) {
      return;
    }
    handleTouchStart(e);
  };

  const handleBottomTouchMove = (e) => {
    if (!touchStartRef.current) return;
    handleTouchMove(e);
  };

  const handleBottomTouchEnd = (e) => {
    if (!touchStartRef.current) return;
    handleTouchEnd();
  };

  return (
    <>
      <div 
        data-tenant-branding-background-color="true"
        className="fixed inset-0 z-0 bg-[#FEFAEE] md:bg-cover md:bg-center md:bg-no-repeat"
        style={{ backgroundImage: 'url(https://kdzuadb2cuistoragedev.blob.core.windows.net/root/templates/src/backgrounds/background_1.png)' }}
      />

      <div className="fixed inset-0 flex justify-center items-start md:items-center z-[100] p-0 md:p-12 overflow-auto md:overflow-hidden overscroll-contain">
        <div 
          className="w-full h-auto md:w-[90vw] md:h-[90vh] md:rounded-tl-[4px] md:rounded-tr-[44px] md:rounded-br-[4px] md:rounded-bl-[44px] md:overflow-hidden md:shadow-[0_10px_30px_rgba(0,0,0,0.3)] shadow-none bg-[#FEFAEE] md:bg-transparent"
          style={isMobile ? { minHeight: 'calc(100vh + 150px)' } : {}}
        >
          
          <div className="relative md:h-full">
            <div className="md:h-full overflow-hidden">
              <div 
                className="flex md:h-full transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform transform-gpu"
                style={{ 
                  transform: `translate3d(-${currentSlide * 100}%, 0, 0)`,
                  touchAction: 'pan-y',
                  backfaceVisibility: 'hidden'
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {landingSlidesData.map((slide, index) => (
                  <div key={`slide-${index}`} className="flex-shrink-0 min-w-full md:h-full">
                    <SlideLayout slide={slide} slideIndex={index} currentSlide={currentSlide} />
                  </div>
                ))}
              </div>
            </div>
            
            <div 
              className="md:hidden w-full bg-[#FEFAEE] pb-8" 
              style={{ 
                paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 16px))',
                touchAction: 'pan-x'
              }}
              onTouchStart={handleBottomTouchStart}
              onTouchMove={handleBottomTouchMove}
              onTouchEnd={handleBottomTouchEnd}
            >
              <div 
                className="w-full px-6 pt-0 pb-4 flex flex-col items-center gap-3"
              >
                <div className="w-full h-2 flex justify-center items-center">
                  <div className="flex gap-[5px]">
                    {landingSlidesData.map((_, index) => (
                      <button
                        key={`mobile-indicator-${index}`}
                        className={`rounded-full border-none transition-all duration-300 w-[6px] h-[6px] ${
                          index === currentSlide 
                            ? 'bg-[#30BC89]' 
                            : 'bg-[rgba(38,43,58,0.3)] hover:bg-[rgba(38,43,58,0.5)]'
                        }`}
                        onClick={() => setCurrentSlide(index)}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>

                <div className="w-full h-[52px] flex gap-3 justify-center mt-4">
                  <button 
                    onClick={handleLoginClick}
                    className="flex-1 max-w-[180px] h-[52px] min-h-[44px] py-[2px] px-[14px] rounded border bg-[#FEFAEE] text-[#2965FB] border-[#2965FB] font-hanken-grotesk font-semibold text-sm transition-all duration-300 hover:opacity-90 active:scale-95"
                    type="button"
                  >
                    {t('login1')}
                  </button>
                  <button 
                    onClick={handleSignupClick}
                    className="flex-1 max-w-[180px] h-[52px] min-h-[44px] py-[2px] px-[14px] rounded border bg-[#2965FB] text-white border-[#2965FB] font-hanken-grotesk font-semibold text-sm transition-all duration-300 hover:opacity-90 active:scale-95"
                    type="button"
                  >
                    {t('sign_up1')}
                  </button>
                </div>

                {landingSlidesData[currentSlide]?.mobileDisclaimers && (
                  <div className="w-full text-center px-1">
                    <div className="font-hanken-grotesk font-normal text-[clamp(9px,2.5vw,11px)] leading-[150%] text-[#262B3A80]">
                      {landingSlidesData[currentSlide].mobileDisclaimers.map((disclaimer, idx) => (
                        <div key={`mobile-disclaimer-${currentSlide}-${idx}`} className={idx > 0 ? 'mt-1' : ''}>
                          {disclaimer.link ? (
                            <>
                              {disclaimer.text.before.split('\n').map((line, i) => (
                                <React.Fragment key={`before-${idx}-${line}`}>
                                  {i > 0 && <br />}
                                  {line}
                                </React.Fragment>
                              ))}
                              <a 
                                href={disclaimer.link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline cursor-pointer hover:opacity-75 text-[#2965FB]"
                              >
                                {disclaimer.link.text}
                              </a>
                              {disclaimer.text.after && disclaimer.text.after.split('\n').map((part, i) => (
                                <React.Fragment key={`after-${idx}-${part}`}>
                                  {i > 0 && <br />}
                                  {part}
                                </React.Fragment>
                              ))}
                            </>
                          ) : (
                            disclaimer.text.split('\n').map((line, i) => (
                              <React.Fragment key={`disclaimer-line-${idx}-${line}`}>
                                {i > 0 && <br />}
                                {line}
                              </React.Fragment>
                            ))
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="hidden md:block absolute lg:bottom-[4vh] md:bottom-[3vh] md:left-[2.5vw] lg:left-[2vw] z-20">
              <div className="flex gap-[clamp(6px,1vw,16px)] w-full max-w-[42vw]">
                <button 
                  onClick={handleLoginClick}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                  className="
                    w-[clamp(250px,25vw,380px)]
                    h-[clamp(48px,5.5vh,56px)]
                    pt-[2px] pr-[14px] pb-[2px] pl-[14px] 
                    rounded-[4px] border border-[#FEFAEE] 
                    font-hanken-grotesk font-semibold 
                    text-[clamp(16px,1.5vw,20px)]
                    transition-all duration-300 hover:opacity-90 active:scale-95 
                    bg-[#0C4437] text-[#FEFAEE] shadow-sm hover:shadow-md 
                    whitespace-nowrap opacity-100
                  "
                  type="button"
                >
                  {t('login1')}
                </button>
                <button 
                  onClick={handleSignupClick}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                  className="
                    w-[clamp(250px,25vw,380px)]
                    h-[clamp(48px,5.5vh,56px)]
                    pt-[4px] pr-[14px] pb-[4px] pl-[14px] 
                    rounded-[4px] border border-[#FEFAEE] 
                    font-hanken-grotesk font-semibold 
                    text-[clamp(16px,1.5vw,20px)]
                    transition-all duration-300 hover:opacity-90 active:scale-95 
                    bg-[#FEFAEE] text-[#262B3A] shadow-sm hover:shadow-md 
                    whitespace-nowrap opacity-100
                  "
                  type="button"
                >
                  {t('sign_up1')}
                </button>
              </div>
            </div>

            <div className="hidden md:block absolute bottom-[clamp(2vh,3vh,4vh)] left-[clamp(55%,60%,65%)] z-20">
              <button 
                className="cursor-pointer transition-opacity hover:opacity-70"
                onClick={goToPrevSlide}
                aria-label="Previous Slide"
              >
                <img 
                  src={ArrowPrev} 
                  alt="Previous" 
                  className="w-[clamp(24px,3vw,40px)] h-[clamp(24px,3vw,40px)]"
                />
              </button>
            </div>
            
            <div className="hidden md:block absolute bottom-[clamp(2vh,3vh,4vh)] right-[clamp(1vw,2vw,3vw)] z-20">
              <button 
                className="cursor-pointer transition-opacity hover:opacity-70"
                onClick={goToNextSlide}
                aria-label="Next Slide"
              >
                <img 
                  src={ArrowNext} 
                  alt="Next" 
                  className="w-[clamp(24px,3vw,40px)] h-[clamp(24px,3vw,40px)]"
                />
              </button>
            </div>

            <div className="hidden md:block absolute bottom-[clamp(4.5vh,5.5vh,6.5vh)] right-[clamp(15%,20%,25%)] z-20">
              <div className="flex gap-[clamp(4px,0.6vw,8px)]">
                {landingSlidesData.map((_, index) => (
                  <button
                    key={`desktop-indicator-${index}`}
                    className={`rounded-full border-none cursor-pointer transition-all duration-300 w-[clamp(4px,0.5vw,8px)] h-[clamp(4px,0.5vw,8px)] ${
                      index === currentSlide 
                        ? 'bg-[#30BC89]' 
                        : 'bg-[rgba(38,43,58,0.3)] hover:bg-[rgba(38,43,58,0.5)]'
                    }`}
                    onClick={() => setCurrentSlide(index)}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LandingCarousel;
